import LoadingComponent from 'Components/HelperComponents/LoadingComponent'
import { useAccreditacioEntities } from 'hooks/useAccreditacioEntities'
import Button from 'NewVersion/components/UI/Buttons/Button'
import { GoBackButton } from 'NewVersion/components/UI/GoBack/GoBackButton'
import 'NewVersion/styles/Acreditacions.scss'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { CREATE_ENTITY_PATH, REGISTER_ENTITY_PATH } from 'Routes/Paths'
import { ENS_TYPES_OBJ } from 'utils/entityUtils'
import { getEntityRouteId } from './utils'

const getReuseEntityTypeLabel = (entity, t) =>
  entity.tipus === ENS_TYPES_OBJ.artista.text
    ? t('entity_artist_title', 'Artist, group or company')
    : t(entity.tipus, entity.tipus)

const ReuseEntityPreview = ({ entity, onContinue, t }) => {
  if (!entity) {
    return null
  }

  return (
    <div className="reuse-entity-preview" data-test="reuse-selected-entity">
      <p className="reuse-entity-preview__label">{t('reuse_selected_entity_label')}</p>
      <div className="reuse-entity-preview__card">
        <div className="reuse-entity-preview__avatar">
          {entity.title?.[0]?.toUpperCase() || '?'}
        </div>
        <div className="reuse-entity-preview__content">
          <h3 className="reuse-entity-preview__title" data-test="reuse-entity-title">
            {entity.title}
          </h3>
          {entity.tipus && (
            <span className="reuse-entity-preview__type">{getReuseEntityTypeLabel(entity, t)}</span>
          )}
          <p className="reuse-entity-preview__status">
            {t('entity_not_performing_this_edition', 'Not performing in this edition.')}
          </p>
          {/* <p className="reuse-entity-preview__note">
            {t(
              'reuse_prefill_fields_notice',
              'We will prefill the basic details and you can review them before saving.'
            )}
          </p> */}
        </div>
      </div>
      <Button color="primary" type="button" dataTest="reuse-confirm-btn" onClick={onContinue}>
        {t('reuse_continue_with_entity', 'Continue with this entity')}
      </Button>
    </div>
  )
}

const ProfessionalReusePage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { nonPerforming, loading: loadingEntities, error } = useAccreditacioEntities()
  const [selectedEntityId, setSelectedEntityId] = useState(null)

  useEffect(() => {
    if (!loadingEntities && !error && nonPerforming.length === 0) {
      navigate(REGISTER_ENTITY_PATH, { replace: true })
    }
  }, [error, loadingEntities, navigate, nonPerforming.length])

  const isSingleEntity = nonPerforming.length === 1

  useEffect(() => {
    if (nonPerforming.length === 0) {
      return
    }

    const currentSelectionExists = nonPerforming.some(
      (entity) => getEntityRouteId(entity) === selectedEntityId
    )

    if (!currentSelectionExists) {
      setSelectedEntityId(getEntityRouteId(nonPerforming[0]))
    }
  }, [nonPerforming, selectedEntityId])

  const selectedEntity = useMemo(() => {
    return (
      nonPerforming.find((entity) => getEntityRouteId(entity) === selectedEntityId) ||
      nonPerforming[0] ||
      null
    )
  }, [nonPerforming, selectedEntityId])

  const navigateToReuse = (entity) => {
    navigate(`${CREATE_ENTITY_PATH}/professional`, {
      state: { initialData: entity },
    })
  }

  if (loadingEntities) {
    return <LoadingComponent />
  }

  if (error) {
    return (
      <div className="acreditacions page-wrapper">
        <h1>{t('acreditacions')}</h1>
        <p>
          {t('error_loading_accreditation', 'An error occurred loading your accreditation data')}
        </p>
      </div>
    )
  }

  if (nonPerforming.length === 0) {
    return <LoadingComponent />
  }

  return (
    <div className="acreditacions page-wrapper">
      <GoBackButton
        text={t('back', 'Back')}
        onClick={() => navigate(REGISTER_ENTITY_PATH)}
        dataTest={isSingleEntity ? 'reuse-back-btn' : 'accreditation-entity-back-btn'}
      />

      <div className="reuse-page-header">
        <h1>{t('reuse_entity_data', 'Reuse existing entity data')}</h1>
      </div>

      <div
        className="reuse-choice-layout"
        data-test={isSingleEntity ? 'reuse-single-entity' : 'reuse-multi-entity'}
      >
        <section className="reuse-option-card reuse-option-card--existing">
          {isSingleEntity ? (
            <ReuseEntityPreview
              entity={nonPerforming[0]}
              onContinue={() => navigateToReuse(nonPerforming[0])}
              t={t}
            />
          ) : (
            <div className="reuse-compare-layout">
              <div className="reuse-entity-list">
                <p className="reuse-entity-list__label">
                  {t('select_entity_to_reuse', 'Select entity to reuse')}
                </p>
                <div className="reuse-entity-list__items">
                  {nonPerforming.map((entity) => {
                    const routeId = getEntityRouteId(entity)
                    const isSelected = routeId === getEntityRouteId(selectedEntity)

                    return (
                      <button
                        key={routeId}
                        type="button"
                        className={`reuse-entity-option ${
                          isSelected ? 'reuse-entity-option--selected' : ''
                        }`}
                        data-test="accreditation-entity-card"
                        onClick={() => setSelectedEntityId(routeId)}
                        aria-pressed={isSelected}
                      >
                        <div className="reuse-entity-option__avatar">
                          {entity.title?.[0]?.toUpperCase() || '?'}
                        </div>
                        <div className="reuse-entity-option__content">
                          <p className="reuse-entity-option__title">{entity.title}</p>
                          {entity.tipus && (
                            <span className="reuse-entity-option__type">
                              {getReuseEntityTypeLabel(entity, t)}
                            </span>
                          )}
                          <p className="reuse-entity-option__status">
                            {t(
                              'entity_not_performing_this_edition',
                              'Not performing in this edition.'
                            )}
                          </p>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>

              <ReuseEntityPreview
                entity={selectedEntity}
                onContinue={() => navigateToReuse(selectedEntity)}
                t={t}
              />
            </div>
          )}
        </section>

        <section className="reuse-option-card reuse-option-card--new">
          <Button
            color="primary"
            variant="outlined"
            type="button"
            dataTest="reuse-create-new-btn"
            onClick={() => navigate(`${CREATE_ENTITY_PATH}/professional`)}
          >
            {t('create_new_professional', 'Create new professional entity')}
          </Button>
        </section>
      </div>
    </div>
  )
}

export default ProfessionalReusePage
